import React from 'react';
import { observer } from 'mobx-react';
import { WithNamespaces, withNamespaces } from 'react-i18next';
import { SectionTitle } from 'apps/group/styles';

import { FullWidthDivider } from 'legacyComponents/styled/lib';
import { TotalSeatsBar, BarItem, BarKey } from 'apps/payment/styles';
import { SubscriptionType } from 'constants/subscription';
import { Spinner } from 'components';

type ConversionUsageProps = {
  conversionUsage: {
    usedConversionCount: number;
  };
  flags: { ConversionLimit: boolean };
  loading: boolean;
  subscriptionType: SubscriptionType;
} & WithNamespaces;

export const ConversionUsage = withNamespaces()(
  observer(({ t, loading, conversionUsage }: ConversionUsageProps) => {
    if (conversionUsage?.usedConversionCount == null) {
      return <></>;
    }

    const { usedConversionCount = 0 } = conversionUsage || {};

    const fixTotal = 5;

    const total =
      conversionUsage.usedConversionCount > 5
        ? conversionUsage.usedConversionCount
        : 5;

    if (!total) return null;

    const unused = total - usedConversionCount;

    const unusedBars = [];
    const usedBars = [];

    for (let i = 0; i < unused; i++) {
      unusedBars.push(
        <BarItem
          key={`unused-conversion-${i}`}
          data-testid={`billingSettingsPage-conversion-bar-${i}`}
          totalBarItems={total}
          last={i === unused - 1}
          first={i === 0 && usedConversionCount === 0}
        />,
      );
    }

    for (let i = 0; i < usedConversionCount; i++) {
      usedBars.push(
        <BarItem
          key={`available-${i}`}
          data-testid={`billingSettingsPage-availableConversion-bar-${i}`}
          active={usedConversionCount < fixTotal}
          exceeded={usedConversionCount >= fixTotal}
          totalBarItems={total}
          first={i === 0}
          last={i === usedConversionCount - 1 && usedConversionCount === total}
        />,
      );
    }

    return (
      <div className='mt-8 w-full text-black font-hel'>
        <SectionTitle style={{ marginBottom: 0 }} level={2} size='medium'>
          {t('Conversion Usage')}
        </SectionTitle>
        <FullWidthDivider />
        <div className='flex justify-between mb-2 mt-5'>
          <b
            className='text-black'
            data-testid='billingSettingsPage-totalUsedSeats-text'
          >
            {t(`${fixTotal} Conversions:`)}
          </b>
          <div className='text-black flex justify-around'>
            {usedConversionCount > 0 && (
              <div
                className='flex items-center mr-1 text-black'
                data-testid='billingSettingsPage-totalAssignedSeats-text'
              >
                <BarKey
                  active={usedConversionCount < fixTotal}
                  exceeded={usedConversionCount >= fixTotal}
                />{' '}
                {usedConversionCount} Used
              </div>
            )}
            {unused > 0 && (
              <div
                className='flex items-center text-black'
                data-testid='billingSettingsPage-totalInvitedSeats-text'
              >
                <BarKey deepGray /> {unused} Available
              </div>
            )}
          </div>
        </div>
        {loading ? (
          <Spinner margin='20px auto' />
        ) : (
          <TotalSeatsBar>
            {usedBars}
            {unusedBars}
          </TotalSeatsBar>
        )}
      </div>
    );
  }),
);
